"use client";

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  GroupVariablePublic,
  GroupVariablesService,
} from "@/services/Spotflow/groupVariables";
import {
  WorkflowVariablePublic,
  WorkflowVariablesService,
} from "@/services/Spotflow/workflowVariables";
import type { VariableValueType } from "@/services/Spotflow/variableShape";

export type SecretScope = "flow" | "workspace";

export interface SecretSource {
  ref: string;
  scope: SecretScope;
  id: string;
  name: string;
  dataType: string;
  raw: WorkflowVariablePublic | GroupVariablePublic;
}

// Maps a stored variable `value_type` to the editor's binding data type so the
// variable picker filters/labels it like any other typed output.
export function valueTypeToDataType(
  valueType: VariableValueType | undefined
): string {
  switch (valueType) {
    case "number":
      return "number";
    case "boolean":
      return "bool";
    case "datetime":
      return "datetime";
    case "file":
      return "File";
    case "object":
      return "object";
    default:
      return "str";
  }
}

const STALE_TIME = 5 * 60 * 1000;
const NO_WORKFLOW_VARS: WorkflowVariablePublic[] = [];
const NO_GROUP_VARS: GroupVariablePublic[] = [];

export function useSecretsSources(
  groupId: string | undefined,
  workflowId: string | undefined
): {
  secrets: SecretSource[];
  isLoading: boolean;
} {
  const { data: workflowVars = NO_WORKFLOW_VARS, isLoading: loadingWorkflow } =
    useQuery({
      queryKey: ["workflow-variables", groupId, workflowId],
      queryFn: () =>
        WorkflowVariablesService.list(groupId as string, workflowId as string),
      enabled: !!groupId && !!workflowId,
      staleTime: STALE_TIME,
    });
  const { data: workspaceVars = NO_GROUP_VARS, isLoading: loadingWorkspace } =
    useQuery({
      queryKey: ["group-variables", groupId],
      queryFn: () => GroupVariablesService.list(groupId as string),
      enabled: !!groupId,
      staleTime: STALE_TIME,
    });

  const secrets = useMemo<SecretSource[]>(() => {
    const out: SecretSource[] = [];
    for (const v of workflowVars) {
      out.push({
        ref: `__secrets__.flow:${v.id}`,
        scope: "flow",
        id: v.id,
        name: v.name,
        dataType: valueTypeToDataType(v.value_type),
        raw: v,
      });
    }
    for (const v of workspaceVars) {
      out.push({
        ref: `__secrets__.workspace:${v.id}`,
        scope: "workspace",
        id: v.id,
        name: v.name,
        dataType: valueTypeToDataType(v.value_type),
        raw: v,
      });
    }
    return out;
  }, [workflowVars, workspaceVars]);

  return { secrets, isLoading: loadingWorkflow || loadingWorkspace };
}

export function parseSecretRef(
  ref: string | undefined
): { scope: SecretScope; id: string } | null {
  if (!ref || !ref.startsWith("__secrets__.")) return null;
  const tail = ref.slice("__secrets__.".length);
  const sep = tail.indexOf(":");
  if (sep <= 0) return null;
  const scope = tail.slice(0, sep);
  const id = tail.slice(sep + 1);
  if (scope !== "flow" && scope !== "workspace") return null;
  return { scope, id };
}
